import { measurementOn } from '@shared/analytics/market';
import { hasConsent, onConsentChange } from '@shared/consent';
import { isDatedAhead } from '@shared/lib/storedTime';

/**
 * DIT-513 — remembering where a visitor came from, long enough to tell the server.
 *
 * **Why this is not read at submit.** The parameters arrive on the first page —
 * `/nl/?gclid=…` from an advertisement — and the visitor then reads the pricing
 * page, opens the funnel, and submits from a URL that carries nothing. So they
 * are taken on the first load that carries them and kept until the form is sent.
 * The server takes it from there, because the verification e-mail is frequently
 * opened in another browser where none of this exists.
 *
 * **Each kind of value follows its own consent.** A click identifier (`gclid`,
 * `gbraid`, `wbraid`, `fbclid`) is an advertising identifier and is kept only
 * with the marketing category; the utm values and the analytics client id follow
 * the analytics category. Withdrawing one category removes its values and leaves
 * the other's. The first version kept click identifiers for a visitor who had
 * accepted analytics only, and withdrawing marketing alone cleared nothing.
 *
 * **First touch, for ninety days.** The first journey on record is the one a
 * signup is attributed to, whole, for as long as a click identifier can still be
 * matched to a click — and the period the platform keeps them for. After that the
 * record is gone and the next visit starts a new one. The server counts its own
 * ninety days from the same moment, which is why the journey's age is sent.
 */

const STORAGE_KEY = 'di_attr_v2';

/** The first version's key: a flat object with no capture time, so no expiry. */
const LEGACY_KEYS = ['di_attr_v1'];

export const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Advertising identifiers — the marketing category. */
export const CLICK_IDENTIFIERS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;

/** Campaign labels — the analytics category. */
export const CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/** The parameters worth carrying, in the shape the API accepts. */
export const ATTRIBUTION_PARAMS = [...CLICK_IDENTIFIERS, ...CAMPAIGN_PARAMS] as const;

export type AttributionParam = (typeof ATTRIBUTION_PARAMS)[number];
export type Attribution = Partial<Record<AttributionParam | 'ga_client_id', string>>;

/** What a registration sends: the values, what they were kept under, and since when. */
export type SignupAttribution = Attribution & {
  /**
   * Both answers, always. A block is sent when either category is granted, so its
   * presence cannot tell the server which — and the purchase it reports to
   * Analytics needs exactly that.
   */
  consent?: { analytics: boolean; advertising: boolean };
  /**
   * How long ago the journey was stored, in whole seconds — for a click identifier,
   * the visit that carried the click. An age rather than a time: both ends are read
   * from this browser's clock, so a clock that is months out cannot move it, and the
   * server takes it from its own.
   */
  captured_age_seconds?: number;
};

interface Stored {
  capturedAt: number;
  values: Attribution;
}

function isClickIdentifier(name: string): boolean {
  return (CLICK_IDENTIFIERS as readonly string[]).includes(name);
}

function allowed(name: string): boolean {
  return isClickIdentifier(name) ? hasConsent('marketing') : hasConsent('analytics');
}

/**
 * Consent to either category, on a market where measurement is switched on. Off,
 * nothing is kept and a registration says `attribution: null`: the record exists to
 * report the first payment to Google, which the client's order puts after the
 * reviewed banner and policy (`measurementOn`).
 */
function mayKeepAnything(): boolean {
  return measurementOn() && (hasConsent('marketing') || hasConsent('analytics'));
}

/** Only the values the visitor's current consent covers. */
function permitted(values: Attribution): Attribution {
  const out: Attribution = {};
  for (const [name, value] of Object.entries(values)) {
    if (value && allowed(name)) out[name as keyof Attribution] = value;
  }
  return out;
}

/**
 * What an address carries. Parameter names are matched without regard to case —
 * `?GCLID=` is the same click — and the first non-empty occurrence of a name wins.
 */
export function attributionFromSearch(search: string): Attribution {
  const found: Attribution = {};
  new URLSearchParams(search).forEach((value, rawName) => {
    const name = rawName.toLowerCase();
    if (!(ATTRIBUTION_PARAMS as readonly string[]).includes(name)) return;
    const key = name as AttributionParam;
    if (!value || found[key] !== undefined) return;
    found[key] = value;
  });
  return found;
}

function fromLocation(): Attribution {
  if (typeof window === 'undefined') return {};
  return attributionFromSearch(window.location.search);
}

/**
 * The analytics client id, if analytics is running and has set its cookie. Read
 * from the cookie rather than asked of `gtag`, because that call is asynchronous
 * and this runs inside a form submission.
 */
function gaClientId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(\d+\.\d+)/);
    return match?.[1];
  } catch {
    // An opaque origin — a sandboxed frame without `allow-same-origin` — throws on
    // the cookie read itself. No client id is the answer, not a failed signup.
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* a private window, or storage the browser refuses */
  }
}

/**
 * The stored journey, or null. Anything that is not what this module writes —
 * `"null"`, an array, a value from another version, a record past its window — is
 * removed and read as nothing, so a corrupted entry cannot take the page down.
 */
function read(): Stored | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  if (!isRecord(parsed) || typeof parsed.capturedAt !== 'number' || !isRecord(parsed.values)) {
    remove(STORAGE_KEY);
    return null;
  }
  // Past its window, or dated further ahead of this browser's clock than drift
  // explains: a record written while the clock was wrong would otherwise never
  // expire. An infinite time — `1e999` parses as one — fails one test or the other,
  // and never reaches the age sent with a registration.
  if (Date.now() - parsed.capturedAt > ATTRIBUTION_TTL_MS || isDatedAhead(parsed.capturedAt)) {
    remove(STORAGE_KEY);
    return null;
  }
  const values: Attribution = {};
  for (const name of ATTRIBUTION_PARAMS) {
    const value = parsed.values[name];
    if (typeof value === 'string' && value) values[name] = value;
  }
  return { capturedAt: parsed.capturedAt, values };
}

function write(stored: Stored): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* see remove() */
  }
}

/**
 * What the visitor's ENTRY address carried, kept in memory only.
 *
 * The banner is answered whenever the visitor pleases — routinely after they have
 * clicked through, and client-side navigation strips the query string. Memory is
 * not storage, so holding the snapshot before consent keeps the contract: nothing
 * is *persisted* until the visitor says yes.
 */
let entrySearch: Attribution | null = null;

/**
 * The capture time of the journey this page load stored, if it stored one.
 *
 * Categories are often accepted one at a time — Analytics from the banner,
 * Advertising from the preferences later — and the first answer stores only what
 * it covers. First touch then found a journey on record and added nothing, so the
 * click of the very visit being recorded was lost. A journey this load stored is
 * still that visit: what a later grant allows from its entry address is added to
 * it, under the same capture time. A journey an earlier load stored stays whole.
 */
let journeyThisLoad: number | null = null;

/**
 * Take whatever this page's address carries and keep what consent allows.
 *
 * **First touch.** A journey already on record, and still inside its window,
 * stays exactly as it is — a later click or a later campaign does not replace it
 * or blend into it. The one exception is the visit this load recorded (see
 * `journeyThisLoad`).
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined' || !mayKeepAnything()) return;

  const existing = read();
  if (existing && Object.keys(existing.values).length > 0) {
    if (existing.capturedAt !== journeyThisLoad) return;
    const values = { ...permitted(entrySearch ?? {}), ...existing.values };
    if (Object.keys(values).length > Object.keys(existing.values).length) {
      write({ capturedAt: existing.capturedAt, values });
    }
    return;
  }

  const current = fromLocation();
  const incoming = permitted(Object.keys(current).length > 0 ? current : (entrySearch ?? {}));
  if (Object.keys(incoming).length === 0) return;

  const capturedAt = Date.now();
  write({ capturedAt, values: incoming });
  journeyThisLoad = capturedAt;
}

/** Bring the stored journey in line with the visitor's answer, then capture. */
function applyConsent(): void {
  if (!mayKeepAnything()) {
    clearAttribution();
    return;
  }
  const existing = read();
  if (existing) {
    const kept = permitted(existing.values);
    if (Object.keys(kept).length === 0) {
      remove(STORAGE_KEY);
    } else if (Object.keys(kept).length !== Object.keys(existing.values).length) {
      // One category withdrawn: its values go, the rest of the journey stays,
      // and so does its capture time — the window does not restart.
      write({ capturedAt: existing.capturedAt, values: kept });
    }
  }
  captureAttribution();
}

/**
 * Capture now, and again whenever consent is answered or changed.
 *
 * The boot pass never clears a journey still inside its window. The consent library
 * announces the stored answer through `onConsentChange`, and that is when a refusal
 * is acted on — clearing here, before the answer is known, would delete a returning
 * visitor's journey on every page load. An expired one does go at boot; see below.
 */
export function startAttributionCapture(): void {
  LEGACY_KEYS.forEach(remove);
  // Swept at start-up whatever the consent state. `read()` is the only thing that
  // drops a record past its window, and every other path to it returns early when
  // consent is not current — a re-asked visitor after a revision bump, or a consent
  // cookie that expired — so their record outlived the 90 days the banner states
  // until they answered again, which may be never. Reading changes nothing else.
  if (typeof window !== 'undefined') read();
  entrySearch = fromLocation();
  captureAttribution();
  onConsentChange(applyConsent);
}

/**
 * What to send with a registration.
 *
 * `null` when the visitor has not agreed to either category — sent as an explicit
 * `attribution: null`, which the API reads as "declined" and which therefore also
 * clears a block an earlier submit of the same registration delivered. An object
 * without values means "consented, arrived without an ad click", a different fact.
 *
 * Otherwise the values, both category answers, and how long ago the journey was
 * captured. An organic visit has no journey and sends no age; the API then counts
 * from the submit.
 */
export function attributionForSignup(): SignupAttribution | null {
  if (typeof window === 'undefined' || !mayKeepAnything()) return null;

  const stored = read();
  const values = permitted(stored?.values ?? {});
  const client = hasConsent('analytics') ? gaClientId() : undefined;
  return {
    ...values,
    ...(client ? { ga_client_id: client } : {}),
    consent: { analytics: hasConsent('analytics'), advertising: hasConsent('marketing') },
    ...(stored && Object.keys(values).length > 0
      ? { captured_age_seconds: Math.max(0, Math.floor((Date.now() - stored.capturedAt) / 1000)) }
      : {}),
  };
}

/** Forget everything kept about this visitor. */
export function clearAttribution(): void {
  remove(STORAGE_KEY);
  LEGACY_KEYS.forEach(remove);
}
